/**
 * The aim indicator — what you are pointing at, and how far the thing you are
 * about to press actually reaches.
 *
 * This is the single biggest readability idea taken from Brawl Stars, and it is
 * not the dot: it is that YOUR OWN range is drawn on the ground BEFORE you
 * commit. Every dimension comes from the roster and the sim's constants — the
 * same numbers `combat.ts` resolves with — so the drawn shape promises exactly
 * what the simulation will do.
 *
 * ── Pass v3: the Brawl Stars read ─────────────────────────────────────────
 *
 * Pass 3's indicator was rejected three times ("aim vfx is ugly"). The four
 * defects, and what each one was:
 *
 *   GREY      a 0.36→0.08 white gradient, normal-blended, and 6 % tinted toward
 *             the floor colour. On the dark teal deck that is a dingy grey, and
 *             it could never be white: the scene is tone-mapped (ACES, exposure
 *             1.25, in the post chain) so a 1.0 white lands at ~0.8. The HUD's
 *             DOM white sat next to it and made it look dirty.
 *   PATTERN   flowing V-chevrons across a long lane read as a conveyor belt.
 *   PLATE     the melee cone started at the body's CENTRE, so a 120° fan of
 *             3.3 u drew as a grey plate under the feet.
 *   LOB       Falcon's landing circle sits ~20 u out — off screen at this
 *             camera — so the thin guide was the only thing drawn, and a thin
 *             guide is a lane.
 *
 * The look now, one rule per defect:
 *
 *   BRIGHT    the fill is an HDR white — linear (FILL_GAIN, FILL_GAIN, FILL_GAIN)
 *             at FILL_ALPHA — blended in the half-float scene target BEFORE the
 *             tone map, so ACES brings it to near-white while the deck's seams
 *             still show through. The rim is the same white, brighter and
 *             near-opaque. The blended pixel stays under the bloom threshold
 *             (2.0 linear luminance; PostProcessing's BLOOM.threshold) on ANY
 *             deck, so it never blooms — `aim-reticle.test.js` pins that
 *             arithmetic. Ultimates are the same look in the v2 gold.
 *   CLEAN     no gradient, no chevrons, no noise, no motion. A flat fill, a
 *             2.5 px feathered edge and a 1.6 px rim, both in SCREEN pixels
 *             (`fwidth`), so a 22 u lane and a 3 u cone have the same edge.
 *             Cone corners are filleted; lanes end in a semicircle.
 *   FROM THE  every directional shape is cut at PLAYER_RADIUS, so it starts at
 *   BODY'S    the body's edge instead of under the feet; every body-centred
 *   EDGE      area (a slam, a pull, a placed zone at the feet) is a RING with a
 *             soft inner band, not a plate.
 *   EXACT     shots → a lane to max range; spreads and melee → a fan (a lunge
 *   SHAPES    adds its corridor); Falcon → a thin solid guide to the landing
 *             point and a FILLED landing circle of the splash radius there;
 *             Starlink → its 12 u zone circle at its fixed 14 u offset, its own
 *             kind; dashes and lines → the lane plus a body-sized end ring
 *             where the body / the line ends; spheres → the reach ring.
 *   FEEDBACK  when a remote body is inside the shape (a CPU mirror of the same
 *             SDFs, `shapeContains`), the fill AND the inner band lift by
 *             HOT_BOOST — a ring has no fill, so lifting the fill alone left
 *             every body-centred area (a slam, a pull) without the feedback.
 *
 * One material graph for every shape (the kind and every dimension are
 * uniforms), built once, compiled by the prewarm, never rebuilt.
 */

import * as THREE from 'three/webgpu';
import { Fn, float, vec2, smoothstep, uniform, mix, positionLocal, length, abs, max, min, dot, sign, clamp, select, fwidth, sqrt } from 'three/tsl';
import { CHARACTERS, isBurst } from '@chaos/protocol';
import { PLAYER_RADIUS, PROJECTILE_SPAWN_OFFSET, GROUND_ZONE_OFFSET } from '@chaos/sim';
import { abilityReach } from '@chaos/bot';
import { session, localPlayer, remotePlayers, roster } from '../state.js';
import { DECAL_LIFT, DECAL_STACK } from './decals.js';
import { projectileLandingDistance } from './Telegraphs.js';
import { onTierChange } from '../PostProcessing.js';

/** Shape kinds, as the material's `kind` uniform reads them. */
const KIND = { lane: 0, sector: 1, lunge: 2, ring: 3, lob: 4, zone: 5 };

/**
 * Basics and skills draw in white; ultimates in the v2 gold (`--gold` in
 * styles.css). The white is PAPER white — the kit's own core white
 * (CelLook `palette().core`, 1 / 0.98 / 0.93) — not #ffffff: a pure white
 * lifted over The Pit's amber deck lifts the blue channel the most and reads
 * lavender (round 2 of the capture loop); a warm white reads as light.
 */
export const WHITE_HEX = 0xfffaed;
export const ULT_GOLD_HEX = 0xffc531;

/**
 * The fill and the rim, as LINEAR HDR gains on the tint and an alpha each.
 * The scene target is half-float and the tone map comes after, so a gain
 * above 1 is what makes a translucent white read WHITE instead of grey. The
 * bound: gain × alpha + (1 − alpha) × 1.0 (a white deck, the worst case) must
 * stay under BLOOM_THRESHOLD, or the indicator would bloom.
 */
export const FILL_GAIN = 1.8;
export const FILL_ALPHA = 0.3;
export const RIM_GAIN = 1.7;
export const RIM_ALPHA = 0.92;
/**
 * A 1 px cel ink just OUTSIDE the rim, at low alpha: invisible on the dark
 * teal deck, and on The Pit's amber deck the one thing that keeps a white or
 * a gold edge from sinking into the floor. The bodies wear the same ink (the
 * outline pass), so it is in the game's language.
 */
export const INK_PX = 1.0;
export const INK_ALPHA = 0.35;
const INK_COLOR = new THREE.Color(0.02, 0.02, 0.05);
/** Mirror of PostProcessing's BLOOM.threshold (linear luminance). */
export const BLOOM_THRESHOLD = 2.0;
/** A body inside the shape lifts the fill and band alphas by this fraction. */
export const HOT_BOOST = 0.4;
/** The edge feather and the rim thickness, in CSS pixels (scaled by the pixel-ratio uniform). */
export const FEATHER_PX = 2.5;
export const RIM_PX = 2.0;
/** The rim's minimum world half-width, so it never dissolves at distance. */
const RIM_MIN = 0.025;
/** Fillet radius (world) at a fan's outer corners. */
const CORNER = 0.35;
/** A ring's soft band inside its rim: width (world) and alpha. */
const INNER_BAND = 0.45;
export const INNER_BAND_ALPHA = 0.22;
/** An offset zone (Starlink) carries a light interior so its 12 u circle reads as an area. */
export const ZONE_FILL_ALPHA = 0.14;
/** The lob's guide: a solid line this wide (world half-width). */
const GUIDE_HALF = 0.05;
/** The show/hide ease (ms): a pop in, a quick fade out; the hot lift's ease. */
const SHOW_MS = 70;
const HIDE_MS = 110;
const HOT_MS = 90;
/** The facing tick at the feet: a small chevron, this far ahead, this long. */
const TICK_AHEAD = 0.85;
const TICK_SIZE = 0.42;
const TICK_ALPHA = 0.55;

let _scene = null;
let _mesh = null;
let _u = null;
let _tick = null;
let _tickU = null;
let _shapeFor = '';
let _shape = null;
let _fadeT = 0;
let _hotT = 0;
let _lastNow = 0;
/** Whether the indicator / the tick is currently drawn (fade > 0). */
let _shown = false;
let _tickShown = false;
let _renderer = null;
/** The shape's resting fill / band alphas; the drawn ones are these × the hot lift. */
let _baseFillA = FILL_ALPHA;
let _baseBandA = 0;
let _stopTierListener = null;

/** The unit indicator plane: x across (−0.5..0.5), z along (0 at the player → −1 at the far end). */
let _planeGeo = null;
function planeGeo() {
  if (!_planeGeo) {
    _planeGeo = new THREE.PlaneGeometry(1, 1);
    _planeGeo.rotateX(-Math.PI / 2);
    _planeGeo.translate(0, 0, -0.5);
  }
  return _planeGeo;
}

/**
 * The ratio the feather and rim are scaled by: rendered pixels per CSS pixel.
 * `fwidth` measures RENDERED pixels, and the canvas renders at the quality
 * tier's ratio (PostProcessing sets it on the renderer), not at the screen's
 * DPR — so the renderer is asked, never `window.devicePixelRatio`.
 */
function pixelScale() {
  return _renderer?.getPixelRatio?.() || 1;
}

/**
 * The indicator material. All in WORLD units on the ground plane, aim down
 * −z: p = (across, along). The mesh is scaled to (boxW, 1, boxL) and the
 * shader undoes that with the same uniforms, so the SDF is exact whatever
 * the shape's size.
 */
function buildIndicatorMaterial() {
  const mat = new THREE.MeshBasicNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  mat.side = THREE.DoubleSide;
  mat.fog = false;
  const u = {
    kind: uniform(0),
    boxW: uniform(1),
    boxL: uniform(1),
    near: uniform(0),
    far: uniform(1),
    nearHalf: uniform(0.5),
    farHalf: uniform(0.5),
    half: uniform(0.5),
    lunge: uniform(0),
    range: uniform(1),
    /** Along-axis position of the body on the plane (world units from the plane's near edge): a ring is centred on it. */
    center: uniform(0),
    /** Radius of the end ring on a dash / line lane; 0 for none. */
    mark: uniform(0),
    fillA: uniform(FILL_ALPHA),
    bandA: uniform(0),
    /** The eased hot state, for the look; the lift itself is baked into fillA / bandA on the CPU. */
    hot: uniform(0),
    fade: uniform(1),
    px: uniform(1),
    tint: uniform(new THREE.Color(1, 1, 1)),
    ink: uniform(INK_COLOR.clone()),
  };
  const px = positionLocal.x.mul(u.boxW);
  const py = positionLocal.z.negate().mul(u.boxL);
  const ax = abs(px);
  // Distance from the BODY: for a ring the plane is pushed back by `center`
  // (boxFor), so the body is at py = center; for everything else, py = 0.
  const rBody = length(vec2(px, py.sub(u.center)));
  // Cut at the body's edge: positive inside the body disc.
  const bodyCut = float(PLAYER_RADIUS).sub(rBody);

  // A smooth max: exact away from the corner, a fillet of ~k/2 at it.
  const smax = Fn(([a, b, k]) => a.add(b).add(sqrt(a.sub(b).mul(a.sub(b)).add(k.mul(k)))).mul(0.5));
  // Lane: a tapered capsule whose near cap is centred at `near` (so it emerges
  // from under the body, round) and whose tip is a semicircle ending exactly at
  // `far`; half-width nearHalf → farHalf along it.
  const lane = Fn(([n, f, nh, fh]) => {
    const b = f.sub(fh);
    const t = py.sub(n).div(max(b.sub(n), float(0.001))).clamp(0, 1);
    return length(vec2(ax, py.sub(mix(n, b, t)))).sub(mix(nh, fh, t));
  });
  // A pie sector of `r` opening `h` radians either side of +y (IQ's sdPie), corners filleted by `k`.
  const pie = Fn(([qx, qy, h, r, k]) => {
    const c = vec2(h.sin(), h.cos());
    const l = length(vec2(qx, qy)).sub(r);
    const proj = clamp(dot(vec2(qx, qy), c), float(0), r);
    const m = length(vec2(qx, qy).sub(c.mul(proj)));
    return smax(l, m.mul(sign(c.y.mul(qx).sub(c.x.mul(qy)))), k);
  });
  const sdLane = lane(u.near, u.far, u.nearHalf, u.farHalf);
  const sdSector = max(pie(ax, py, u.half, u.range, float(CORNER)), bodyCut);
  // The lunge: its corridor starts FLAT at the body's front edge (a round cap
  // there reached a full width behind the body — round 2), then the cone.
  const sdLunge = smax(min(lane(float(0), u.lunge, u.nearHalf, u.nearHalf), pie(ax, py.sub(u.lunge), u.half, u.range, float(CORNER))), float(PLAYER_RADIUS).sub(py), float(CORNER));
  const sdRing = rBody.sub(u.range);
  // The landing / zone circle: centred `far` ahead, radius `range`.
  const sdCircle = length(vec2(px, py.sub(u.far))).sub(u.range);
  const sd = select(u.kind.equal(KIND.lane), sdLane,
    select(u.kind.equal(KIND.sector), sdSector,
      select(u.kind.equal(KIND.lunge), sdLunge,
        select(u.kind.equal(KIND.ring), sdRing, sdCircle))));

  // Screen-space widths: world units per CSS pixel along the SDF's gradient.
  const w = fwidth(sd).max(float(1e-4)).mul(u.px);
  const feather = w.mul(FEATHER_PX);
  const rimHalf = w.mul(RIM_PX).max(float(RIM_MIN));
  // The fill, feathered INSIDE the true edge (exact at the boundary).
  const inside = float(1).sub(smoothstep(feather.negate(), float(0), sd));
  // The rim: a band 2·rimHalf wide whose outer edge is the boundary.
  const rim = float(1).sub(smoothstep(rimHalf, rimHalf.add(w.mul(1.2)), abs(sd.add(rimHalf))));
  // A ring's soft band, rising toward the rim from INNER_BAND inside it.
  const band = smoothstep(float(-INNER_BAND), float(0), sd).mul(inside);
  // The lob's guide: a thin SOLID line from the muzzle to the landing circle.
  const sdGuide = lane(u.near, u.far.sub(u.range), float(GUIDE_HALF), float(GUIDE_HALF));
  const gw = fwidth(sdGuide).max(float(1e-4)).mul(u.px);
  const guide = float(1).sub(smoothstep(float(0), gw.mul(1.5), sdGuide)).mul(select(u.kind.equal(KIND.lob), float(1), float(0)));
  // The end ring on a dash / line: a body-sized circle at `far`.
  const sdMark = abs(length(vec2(px, py.sub(u.far))).sub(u.mark));
  const mw = fwidth(sdMark).max(float(1e-4)).mul(u.px);
  const markHalf = mw.mul(RIM_PX).max(float(RIM_MIN));
  const mark = float(1).sub(smoothstep(markHalf, markHalf.add(mw.mul(1.2)), sdMark)).mul(select(u.mark.greaterThan(0), float(1), float(0)));

  // The ink: a band INK_PX wide just outside the boundary (sd in 0..inkW).
  const inkW = w.mul(INK_PX);
  const ink = float(1).sub(smoothstep(inkW.mul(0.5), inkW.mul(0.5).add(w.mul(1.2)), abs(sd.sub(inkW.mul(0.5)))));

  const line = rim.max(guide).max(mark);
  // fillA and bandA arrive already lifted by the hot state (updateAimReticle).
  const fill = inside.mul(u.fillA).add(band.mul(u.bandA));
  const inkOnly = ink.mul(float(1).sub(line)).mul(float(1).sub(inside));
  mat.colorNode = Fn(() => mix(mix(u.tint.mul(FILL_GAIN), u.tint.mul(RIM_GAIN), line), u.ink, inkOnly))();
  mat.opacityNode = Fn(() => fill.add(line.mul(RIM_ALPHA)).add(inkOnly.mul(INK_ALPHA)).min(1).mul(u.fade))();
  return { mat, u };
}

/** The facing tick: a small chevron pointing down −z, the same HDR white, on a unit plane. */
function buildTickMaterial() {
  const mat = new THREE.MeshBasicNodeMaterial();
  mat.transparent = true;
  mat.depthWrite = false;
  mat.side = THREE.DoubleSide;
  mat.fog = false;
  const u = { fade: uniform(1), tint: uniform(new THREE.Color(1, 1, 1)) };
  const px = positionLocal.x;
  const py = positionLocal.z.negate(); // 0..1 along
  // A chevron: |x| + (1 - y)·0.6 thin band.
  const d = abs(px).add(py.negate().add(1).mul(0.62));
  const band = smoothstep(float(0.58), float(0.5), d).mul(smoothstep(float(0.2), float(0.28), d));
  mat.colorNode = Fn(() => u.tint.mul(FILL_GAIN))();
  mat.opacityNode = Fn(() => band.mul(TICK_ALPHA).mul(u.fade))();
  return { mat, u };
}

/** A fresh indicator mesh (its own material), for the shader prewarm. */
export function buildIndicatorMesh() {
  const { mat } = buildIndicatorMaterial();
  const mesh = new THREE.Mesh(planeGeo(), mat);
  mesh.frustumCulled = false;
  mesh.userData.noOutline = true;
  return mesh;
}

/** A fresh facing-tick mesh, for the shader prewarm. */
export function buildTickMesh() {
  const { mat } = buildTickMaterial();
  const mesh = new THREE.Mesh(planeGeo(), mat);
  mesh.frustumCulled = false;
  mesh.userData.noOutline = true;
  return mesh;
}

/**
 * @param {THREE.Scene} scene
 * @param {THREE.WebGPURenderer} [renderer] whose pixel ratio the screen-space widths follow
 * @param {(fn: Function) => Function} [subscribeTier] the tier-change hook (PostProcessing's, by default)
 */
export function initAimReticle(scene, renderer = null, subscribeTier = onTierChange) {
  _scene = scene;
  _renderer = renderer;
  const built = buildIndicatorMaterial();
  _u = built.u;
  // Both meshes stay in the render list from the first frame, hidden by their
  // `fade` uniform (opacity 0) rather than `visible = false`: a mesh the
  // renderer has never drawn is built — its node-graph cache key, its
  // pipeline — on the frame it first appears, and that frame was the first
  // aimed press of every match (~10 ms, named by the build log). Two
  // transparent quads at opacity 0 cost nothing per frame.
  _mesh = new THREE.Mesh(planeGeo(), built.mat);
  _mesh.frustumCulled = false;
  _mesh.renderOrder = 3;
  _mesh.name = 'aim-indicator';
  _mesh.userData.noOutline = true;
  _u.fade.value = 0;
  _u.px.value = pixelScale();
  // The governor re-sets the renderer's pixel ratio on every tier change
  // (before it calls the listeners), so the widths follow it there.
  _stopTierListener?.();
  _stopTierListener = subscribeTier(() => { if (_u) _u.px.value = pixelScale(); });
  _mesh.scale.set(0.01, 1, 0.01);
  _scene.add(_mesh);
  const tick = buildTickMaterial();
  _tickU = tick.u;
  _tick = new THREE.Mesh(planeGeo(), tick.mat);
  _tick.frustumCulled = false;
  _tick.renderOrder = 3;
  _tick.name = 'aim-tick';
  _tick.userData.noOutline = true;
  _tickU.fade.value = 0;
  _scene.add(_tick);
  _shown = false;
  _tickShown = false;
  _shapeFor = '';
  _shape = null;
  _fadeT = 0;
  _hotT = 0;
  _lastNow = 0;
  // The probe's window on the show/hide rule (the capture harness reads it).
  if (typeof window !== 'undefined') window.__aimDebug = aimReticleLook;
}

/**
 * The shape an ability draws, from its own numbers. Returns null for
 * abilities with nothing to aim (the self buffs). `tint` is white for a
 * basic or a skill and the v2 gold for an ultimate.
 */
export function describeAim(ability, abilityId = ability?.id) {
  if (!ability) return null;
  const hit = ability.hit;
  const tint = ability.slot === 'ultimate' ? ULT_GOLD_HEX : WHITE_HEX;
  if (hit.kind === 'shot') {
    const corridor = hit.radius + PLAYER_RADIUS;
    const far = hit.maxRange + PROJECTILE_SPAWN_OFFSET;
    const spreadRad = (hit.spreadDeg * Math.PI) / 180;
    if (hit.pellets > 1 && !isBurst(hit)) {
      // A spread: the fan, as a sector, padded by the corridor at the edges.
      const half = spreadRad / 2 + Math.atan2(corridor, far);
      return { kind: 'sector', range: far, half, inner: PLAYER_RADIUS, tint };
    }
    // A single or a burst: a lane. The burst's wobble widens the far end.
    return { kind: 'lane', near: PROJECTILE_SPAWN_OFFSET * 0.5, far, nearHalf: corridor, farHalf: corridor + Math.tan(spreadRad / 2) * far, mark: 0, tint };
  }
  if (hit.kind === 'melee_cone') {
    const range = hit.range + PLAYER_RADIUS;
    const half = (hit.halfAngleDeg * Math.PI) / 180;
    const lunge = ability.selfMove ?? 0;
    // The fan starts at the body's edge, not under the feet.
    if (lunge <= 0) return { kind: 'sector', range, half, inner: PLAYER_RADIUS, tint };
    // The lunge corridor (a body that stops the lunge gets cut) and then the cone.
    return { kind: 'lunge', lunge, corridorHalf: PLAYER_RADIUS * 2, range, half, inner: PLAYER_RADIUS, tint };
  }
  if (hit.kind === 'projectile') {
    // A lob can only connect at its landing ring: a filled landing circle of
    // the splash radius there, with a thin solid guide from the muzzle to it.
    return { kind: 'lob', near: PROJECTILE_SPAWN_OFFSET * 0.5, far: projectileLandingDistance(hit), range: hit.radius, tint };
  }
  if (hit.kind === 'ground_zone') {
    const off = GROUND_ZONE_OFFSET[abilityId] ?? 0;
    // Placed at a fixed offset (Starlink): the zone circle exactly there.
    if (off > 0) return { kind: 'zone', far: off, range: hit.radius, tint };
    // Placed at the feet: the ring of what will be there.
    return { kind: 'ring', range: hit.radius, tint };
  }
  if (hit.kind === 'dash_line') {
    // The sim's own rule (combat.ts, dash_line): a self-moving dash runs its
    // selfMove; a ground line runs its length. The lane starts at the body's
    // edge and ends in a body-sized ring where the body / the line ends.
    const len = ability.selfMove && ability.selfMove > 0 ? ability.selfMove : hit.length;
    return { kind: 'lane', near: PLAYER_RADIUS, far: len, nearHalf: hit.width / 2, farHalf: hit.width / 2, mark: PLAYER_RADIUS, tint };
  }
  if (hit.kind === 'sphere') {
    // `abilityReach` is the same function the bots ask "can I hit them?" with.
    return { kind: 'ring', range: Math.max(abilityReach(ability), 1), tint };
  }
  return null;
}

// ── The CPU twins of the shader's SDFs (same formulas, line for line) ──
const smaxJs = (a, b, k) => (a + b + Math.sqrt((a - b) * (a - b) + k * k)) * 0.5;
/** The tapered capsule: near cap centred at `n`, round tip ending exactly at `f`. */
function sdLaneJs(ax, y, n, f, nh, fh) {
  const b = f - fh;
  const t = Math.max(0, Math.min(1, (y - n) / Math.max(b - n, 1e-3)));
  return Math.hypot(ax, y - (n + (b - n) * t)) - (nh + (fh - nh) * t);
}
/** IQ's sdPie, opening `h` either side of +y, corners filleted by `k`. */
function sdPieJs(qx, qy, h, r, k) {
  const cx = Math.sin(h);
  const cy = Math.cos(h);
  const l = Math.hypot(qx, qy) - r;
  const proj = Math.max(0, Math.min(r, qx * cx + qy * cy));
  const m = Math.hypot(qx - cx * proj, qy - cy * proj);
  return smaxJs(l, m * Math.sign(cy * qx - cx * qy), k);
}

/**
 * Whether a point (across, along) in the aim's frame — +along ahead of the
 * body, across to its right — is inside the shape's HIT region. The CPU
 * mirror of the shader's SDFs, so the "a body is inside" lift agrees with
 * the drawing. A lob's guide is not a hit region; only its landing circle is.
 */
export function shapeContains(d, x, y) {
  if (!d) return false;
  const ax = Math.abs(x);
  switch (d.kind) {
    case 'lane': {
      // The same tapered capsule the shader draws.
      return sdLaneJs(ax, y, d.near, d.far, d.nearHalf, d.farHalf) <= 0;
    }
    case 'sector': {
      const r = Math.hypot(x, y);
      return r <= d.range && r >= (d.inner ?? 0) && Math.abs(Math.atan2(x, y)) <= d.half;
    }
    case 'lunge': {
      // The shader's sdLunge exactly: the corridor is a CAPSULE whose round
      // tip ends at `lunge` (tip centre at lunge − corridorHalf), unioned with
      // the cone from the lunge's end, cut flat at the body's front edge.
      const corridor = sdLaneJs(ax, y, 0, d.lunge, d.corridorHalf, d.corridorHalf);
      const cone = sdPieJs(ax, y - d.lunge, d.half, d.range, CORNER);
      return smaxJs(Math.min(corridor, cone), PLAYER_RADIUS - y, CORNER) <= 0;
    }
    case 'ring':
      return Math.hypot(x, y) <= d.range;
    case 'lob':
    case 'zone':
      return Math.hypot(x, y - d.far) <= d.range;
    default:
      return false;
  }
}

/** The bounding box (width, length) a shape needs, so the plane covers it. */
function boxFor(d) {
  const pad = 0.3;
  switch (d.kind) {
    case 'lane': return { w: Math.max(d.nearHalf, d.farHalf, d.mark ?? 0) * 2 + pad, l: d.far + (d.mark ?? 0) + pad };
    case 'sector': return { w: Math.sin(Math.min(d.half, Math.PI / 2)) * d.range * 2 + pad + (d.half > Math.PI / 2 ? d.range * 2 : 0), l: d.range + pad };
    case 'lunge': return { w: Math.max(d.corridorHalf * 2, Math.sin(Math.min(d.half, Math.PI / 2)) * d.range * 2) + pad, l: d.lunge + d.range + pad };
    // Centred on the player: as long behind as ahead, pushed back by half.
    case 'ring': return { w: d.range * 2 + pad, l: d.range * 2 + pad, back: d.range + pad / 2 };
    case 'lob':
    case 'zone': return { w: d.range * 2 + pad, l: d.far + d.range + pad };
    default: return { w: 1, l: 1 };
  }
}

function applyShape(d) {
  const u = _u;
  u.kind.value = KIND[d.kind];
  u.near.value = d.near ?? 0;
  u.far.value = d.far ?? 0;
  u.nearHalf.value = d.kind === 'lunge' ? d.corridorHalf : d.nearHalf ?? 0;
  u.farHalf.value = d.farHalf ?? u.nearHalf.value;
  u.half.value = d.half ?? 0;
  u.lunge.value = d.lunge ?? 0;
  u.range.value = d.range ?? 1;
  u.mark.value = d.mark ?? 0;
  // Directional shapes are filled; a ring is its rim and a soft band; an
  // offset zone is a ring with a light interior.
  _baseFillA = d.kind === 'ring' ? 0 : d.kind === 'zone' ? ZONE_FILL_ALPHA : FILL_ALPHA;
  _baseBandA = d.kind === 'ring' || d.kind === 'zone' ? INNER_BAND_ALPHA : 0;
  u.fillA.value = _baseFillA;
  u.bandA.value = _baseBandA;
  const box = boxFor(d);
  u.boxW.value = box.w;
  u.boxL.value = box.l;
  // The ring's centre sits exactly where the plane was pushed back from: the player.
  u.center.value = box.back ?? 0;
  u.tint.value.setHex(d.tint ?? WHITE_HEX);
  _tickU.tint.value.setHex(WHITE_HEX);
  _mesh.scale.set(box.w, 1, box.l);
  // A ring is centred on the player: the plane runs from +range behind to −range ahead.
  _mesh.userData.back = box.back ?? 0;
  _mesh.userData.kind = d.kind;
}

/** Is any drawn remote body inside the shape, measured in the aim's frame? */
function enemyInside(d, p, yaw) {
  const s = Math.sin(yaw);
  const c = Math.cos(yaw);
  for (const [id, mesh] of remotePlayers) {
    const row = roster.get(id);
    if (row && row.phase !== 'alive') continue;
    const dx = mesh.position.x - p.x;
    const dz = mesh.position.z - p.z;
    // The mesh is rotated by `yaw` about Y; this is that rotation's inverse:
    // across = local +x, along = local −z (the sim's forward at this yaw).
    const across = dx * c - dz * s;
    const along = -(dx * s + dz * c);
    if (shapeContains(d, across, along)) return true;
  }
  return false;
}

/**
 * @param {THREE.Vector3} aimPoint where the aim ray met the ground (unused now: the shape is the aim)
 * @param {string|null} slot ability slot being highlighted, or null for the basic
 * @param {number} groundY
 * @param {number} [yaw] the aim yaw the next press will be sent with
 * @param {boolean} [aiming] the show/hide rule's answer (InputManager.isAiming)
 */
export function updateAimReticle(aimPoint, slot, groundY, yaw = 0, aiming = false) {
  if (!_mesh) return;
  const now = performance.now();
  const dt = _lastNow ? Math.min(0.1, (now - _lastNow) / 1000) : 0;
  _lastNow = now;
  const alive = localPlayer.phase === 'alive';
  const def = CHARACTERS[session.characterId];
  const useSlot = slot ?? 'basic';
  const ability = def ? def.abilities[useSlot] : null;
  const shape = alive && ability ? describeAim(ability, ability.id) : null;
  const want = alive && aiming && !!shape;

  // The ease: a pop in, a quick fade out. Hidden entirely at 0.
  _fadeT += want ? dt * (1000 / SHOW_MS) : -dt * (1000 / HIDE_MS);
  _fadeT = Math.max(0, Math.min(1, _fadeT));
  const p = localPlayer.renderPos;

  // The facing tick: whenever alive and the indicator is not up.
  _tickShown = alive && _fadeT < 1;
  _tickU.fade.value = _tickShown ? 1 - _fadeT : 0;
  if (_tickShown) {
    _tick.position.set(p.x, groundY + DECAL_LIFT, p.z);
    _tick.rotation.y = yaw;
    _tick.scale.set(TICK_SIZE, 1, TICK_SIZE);
    // The plane runs 0..−1 along; push it out to sit ahead of the feet.
    _tick.translateZ(-TICK_AHEAD);
  }

  _shown = _fadeT > 0 && !!shape;
  if (!_shown) {
    _u.fade.value = 0;
    _hotT = 0;
    _u.hot.value = 0;
    return;
  }
  const key = `${session.characterId}:${useSlot}`;
  if (key !== _shapeFor) {
    _shapeFor = key;
    _shape = shape;
    applyShape(shape);
  }
  _u.fade.value = _fadeT * (2 - _fadeT); // ease-out
  // A body inside the shape lifts the fill, eased so a body crossing the edge does not flicker it.
  const hot = enemyInside(_shape, p, yaw);
  _hotT += hot ? dt * (1000 / HOT_MS) : -dt * (1000 / HOT_MS);
  _hotT = Math.max(0, Math.min(1, _hotT));
  _u.hot.value = _hotT;
  // The lift scales everything the shape fills — the plate AND a ring's band —
  // so every kind answers a body inside it.
  const lift = 1 + _hotT * HOT_BOOST;
  _u.fillA.value = _baseFillA * lift;
  _u.bandA.value = _baseBandA * lift;
  _mesh.position.set(p.x, groundY + DECAL_LIFT + DECAL_STACK, p.z);
  // Geometry is authored down -Z (the sim's forward at yaw 0); the sim's
  // forward is (-sin yaw, -cos yaw), so rotating by `yaw` about Y maps it.
  _mesh.rotation.y = yaw;
  if (_mesh.userData.back > 0) _mesh.translateZ(_mesh.userData.back);
}

const _centerScratch = new THREE.Vector3();
/** The world point the shader treats as the ring's centre: geometry z = −center / boxL. */
function ringCenterWorld() {
  _mesh.updateMatrixWorld(true);
  _centerScratch.set(0, 0, -_u.center.value / Math.max(_u.boxL.value, 1e-6));
  return _mesh.localToWorld(_centerScratch).toArray();
}

/** For tests and the capture probe: what is drawn right now. */
export function aimReticleLook() {
  if (!_mesh) return null;
  return {
    visible: _shown,
    fade: _u.fade.value,
    kind: _shown ? _mesh.userData.kind : null,
    tickVisible: _tickShown,
    hot: _u.hot.value,
    dims: { near: _u.near.value, far: _u.far.value, nearHalf: _u.nearHalf.value, farHalf: _u.farHalf.value, half: _u.half.value, lunge: _u.lunge.value, range: _u.range.value, center: _u.center.value, boxL: _u.boxL.value, mark: _u.mark.value },
    // Where the shape's ring centre lands in the world (the shader's py = center).
    discCenter: _mesh.userData.kind === 'ring' ? ringCenterWorld() : null,
    style: {
      fill: { alpha: _u.fillA.value, gain: FILL_GAIN },
      band: { alpha: _u.bandA.value },
      rim: { alpha: RIM_ALPHA, gain: RIM_GAIN, px: RIM_PX },
      ink: { alpha: INK_ALPHA, px: INK_PX },
      featherPx: FEATHER_PX,
      pixelRatio: _u.px.value,
      pattern: 'none',
      gradient: 'none',
    },
    tint: _u.tint.value.getHex(),
  };
}

export function disposeAimReticle() {
  _stopTierListener?.();
  _stopTierListener = null;
  _renderer = null;
  for (const m of [_mesh, _tick]) {
    if (!m) continue;
    m.removeFromParent();
    m.material.dispose();
  }
  _mesh = null;
  _tick = null;
  _u = null;
  _tickU = null;
  _shapeFor = '';
  _shape = null;
  _fadeT = 0;
  _hotT = 0;
  _lastNow = 0;
  _shown = false;
  _tickShown = false;
  _baseFillA = FILL_ALPHA;
  _baseBandA = 0;
}
